import {
  DatacenterV2,
  EnvironmentRequest,
  ExtendedVCenter,
  RequestId,
  VCenter,
  AutoDiscoveredVms
} from 'vrlcm-gui-types';
import { Http } from 'vrlcm-gui-utils';
import { LcopsApiUri } from './uri';

export class DatacenterApi {
  public static async getDatacenters() {
    const response = await Http.get<DatacenterV2[]>(
      LcopsApiUri.DataCenterV2.DATACENTERS_URL
    );
    return response.data;
  }

  public static async getDatacentersWithChildren() {
    const datacenters = await DatacenterApi.getDatacenters();
    return Promise.all<DatacenterV2>(
      datacenters.map(async (datacenter: DatacenterV2) => {
        const vCenters = await DatacenterApi.getVCenters(
          datacenter.dataCenterVmid
        );
        return { ...datacenter, vCenters, environments: [] };
      })
    );
  }

  public static async getDatacenter(vmid: string) {
    const url = `${LcopsApiUri.DataCenterV2.DATACENTERS_URL}/${vmid}`;
    const response = await Http.get(url);
    return response.data;
  }

  public static async addDatacenter(
    dataCenter: DatacenterV2
  ): Promise<DatacenterV2> {
    const { dataCenterName, primaryLocation } = dataCenter;
    const response = await Http.post<DatacenterV2>(
      LcopsApiUri.DataCenterV2.DATACENTERS_URL,
      { dataCenterName, primaryLocation }
    );
    const { dataCenterVmid } = response.data;
    return { ...dataCenter, dataCenterVmid };
  }

  public static async editDatacenter(dataCenter: DatacenterV2) {
    const { dataCenterName, dataCenterVmid, primaryLocation } = dataCenter;
    await Http.put(LcopsApiUri.DataCenterV2.DATACENTER_URL(dataCenterVmid), {
      dataCenterName,
      primaryLocation
    });
    return dataCenter;
  }

  public static updateDatacenter(dataCenter: DatacenterV2, editMode: boolean) {
    if (editMode) {
      return DatacenterApi.editDatacenter(dataCenter);
    } else {
      return DatacenterApi.addDatacenter(dataCenter);
    }
  }

  public static async deleteDatacenter(dataCenterVmid: string) {
    const response = await Http.delete<RequestId>(
      LcopsApiUri.DataCenterV2.DATACENTER_URL(dataCenterVmid)
    );
    return response.data;
  }

  public static async getVmsInDataCenter(dataCenterVmid: string) {
    const response = await Http.get<AutoDiscoveredVms>(
      LcopsApiUri.DataCenterV2.DATACENTER_VMS_URL(dataCenterVmid)
    );
    return response.data;
  }

  public static async getVCenters(dataCenterVmid: string) {
    const response = await Http.get<VCenter[]>(
      LcopsApiUri.DataCenterV2.VCENTERS_URL(dataCenterVmid)
    );
    return response.data;
  }

  public static async validateVCenter(
    dataCenterVmid: string,
    vCenter: VCenter
  ) {
    const response = await Http.post<RequestId>(
      LcopsApiUri.DataCenterV2.VALIDATE_VCENTER_URL(dataCenterVmid),
      vCenter
    );
    return response.data;
  }

  public static async editVCenter(dataCenterVmid: string, vCenter: VCenter) {
    const response = await Http.put<RequestId>(
      LcopsApiUri.DataCenterV2.VCENTER_URL(dataCenterVmid, vCenter.vCenterName),
      vCenter
    );
    return response.data;
  }

  public static async addVCenter(dataCenterVmid: string, vCenter: VCenter) {
    const response = await Http.post<RequestId>(
      LcopsApiUri.DataCenterV2.VCENTERS_URL(dataCenterVmid),
      vCenter
    );
    return response.data;
  }

  public static updateVCenter(
    dataCenterVmid: string,
    vCenter: VCenter,
    editMode: boolean
  ) {
    if (editMode) {
      return DatacenterApi.editVCenter(dataCenterVmid, vCenter);
    } else {
      return DatacenterApi.addVCenter(dataCenterVmid, vCenter);
    }
  }

  public static async triggerVCenterDataCollection(
    dataCenterVmid: string,
    vCenter: VCenter
  ) {
    const response = await Http.post<RequestId>(
      LcopsApiUri.DataCenterV2.VCENTER_DATA_COLLECTION_URL(
        dataCenterVmid,
        vCenter.vCenterName
      )
    );
    return response.data;
  }

  public static async validateImportVCenters(
    dataCenterVmid: string,
    file: FileList
  ) {
    const formData = new FormData();
    formData.append('vCenterImportFile', file[0]);
    const response = await Http.post(
      LcopsApiUri.DataCenterV2.VALIDATE_IMPORT_VCENTERS_URL(dataCenterVmid),
      formData
    );
    return response.data;
  }

  public static async importVCenters(dataCenterVmid: string, file: File) {
    const formData = new FormData();
    formData.append('vCenterImportFile', file);
    const response = await Http.post(
      LcopsApiUri.DataCenterV2.IMPORT_VCENTERS_URL(dataCenterVmid),
      formData
    );
    return response.data;
  }

  public static async getEnvironmentsInDatacenter(dataCenterVmid: string) {
    const response = await Http.get<EnvironmentRequest[]>(
      LcopsApiUri.DataCenterV2.GET_DATACENTER_ENVIRONMENTS_URL(dataCenterVmid)
    );
    return response.data;
  }

  public static async getEnvironmentsAssociatedWithVCenter(
    dataCenterVmid: string,
    vCenterName: string
  ) {
    const response = await Http.get<EnvironmentRequest[]>(
      LcopsApiUri.DataCenterV2.GET_VCENTER_ENVIRONMENTS_URL(
        dataCenterVmid,
        vCenterName
      )
    );
    return response.data;
  }

  public static async getVCenter(dataCenterVmid: string, vCenterName: string) {
    const response = await Http.get<ExtendedVCenter>(
      LcopsApiUri.DataCenterV2.VCENTER_URL(dataCenterVmid, vCenterName)
    );
    return response.data;
  }

  public static async deleteVCenter(
    dataCenterVmid: string,
    vCenterName: string
  ) {
    const response = await Http.delete<RequestId>(
      LcopsApiUri.DataCenterV2.VCENTER_URL(dataCenterVmid, vCenterName)
    );
    return response.data;
  }

  public static async hasVMwareSSO(
    dataCenterVmid: string,
    vCenterName: string
  ) {
    const response = await Http.get<boolean>(
      LcopsApiUri.DataCenterV2.HAS_VMWARESSO_URL(dataCenterVmid, vCenterName)
    );
    return response.data;
  }
}
